// Sketch Utilities - Helper functions for sketch operations
// Organized for Supabase Migration

import { SKETCH_CATEGORIES } from '../data/sketchCategories.js';

// Generate points array from parameters for different sketch types
export function generatePointsFromParams(sketch, params) {
  switch (sketch.name) {
    case "Triangle":
    case "Detected Triangle":
    case "Triangle with Angles":
    case "Midpoint Theorem":
    case "Triangle with Interior Angles":
    case "Bearing & Direction Triangle":
      return [
        { name: "A", x: params.pointA_x, y: params.pointA_y, z: null },
        { name: "B", x: params.pointB_x, y: params.pointB_y, z: null },
        { name: "C", x: params.pointC_x, y: params.pointC_y, z: null }
      ];
    case "Circle":
    case "Detected Circle":
      return [
        { name: "Center", x: params.centerX, y: params.centerY, z: null }
      ];
    case "Cyclic Quadrilateral":
      return [
        { name: "P", x: params.centerX + params.radius * Math.cos(params.pointP_angle * Math.PI / 180), y: params.centerY + params.radius * Math.sin(params.pointP_angle * Math.PI / 180), z: null },
        { name: "Q", x: params.centerX + params.radius * Math.cos(params.pointQ_angle * Math.PI / 180), y: params.centerY + params.radius * Math.sin(params.pointQ_angle * Math.PI / 180), z: null },
        { name: "R", x: params.centerX + params.radius * Math.cos(params.pointR_angle * Math.PI / 180), y: params.centerY + params.radius * Math.sin(params.pointR_angle * Math.PI / 180), z: null },
        { name: "S", x: params.centerX + params.radius * Math.cos(params.pointS_angle * Math.PI / 180), y: params.centerY + params.radius * Math.sin(params.pointS_angle * Math.PI / 180), z: null }
      ];
    case "Circle with Intersecting Chords":
    case "Pentagon in Circle":
      return [
        { name: "Center", x: params.centerX, y: params.centerY, z: null }
      ];
    case "Parabola":
    case "Detected Parabola":
    case "Function Roots & Horizontal Line":
    case "Detected Function":
      return [
        { name: "Vertex", x: params.vertexX || params.centerX || 200, y: params.vertexY || params.horizontalLineY || 150, z: null }
      ];
    case "Square Pyramid":
    case "Detected Pyramid":
    case "Pyramid Multiple Views":
      const { baseSize, height, offsetX, offsetY, offsetZ } = params;
      const halfBase = baseSize / 2;
      return [
        { name: "Apex", x: offsetX || 0, y: (offsetY || 0) + height, z: offsetZ || 0 },
        { name: "Base1", x: (offsetX || 0) - halfBase, y: offsetY || 0, z: (offsetZ || 0) - halfBase },
        { name: "Base2", x: (offsetX || 0) + halfBase, y: offsetY || 0, z: (offsetZ || 0) - halfBase },
        { name: "Base3", x: (offsetX || 0) + halfBase, y: offsetY || 0, z: (offsetZ || 0) + halfBase },
        { name: "Base4", x: (offsetX || 0) - halfBase, y: offsetY || 0, z: (offsetZ || 0) + halfBase }
      ];
    case "Double Triangle Construction":
      return [
        { name: "A", x: params.pointA_x, y: params.pointA_y, z: null },
        { name: "B", x: params.pointB_x, y: params.pointB_y, z: null },
        { name: "C", x: params.pointC_x, y: params.pointC_y, z: null },
        { name: "D", x: params.pointD_x, y: params.pointD_y, z: null },
        { name: "X", x: params.pointX_x, y: params.pointX_y, z: null }
      ];
    default:
      return [];
  }
}

// Export sketch to JSON format
export function exportSketchAsJSON(sketch, currentParams) {
  const sketchData = {
    id: sketch.id,
    name: sketch.name,
    category: sketch.category,
    description: sketch.description,
    coordinates: sketch.coordinates,
    type: sketch.type,
    points: generatePointsFromParams(sketch, currentParams),
    properties: {
      shape: sketch.name.toLowerCase(),
      ...currentParams
    },
    parameters: currentParams,
    parameter_config: sketch.parameter_config,
    export_date: new Date().toISOString(),
    exported_from: "Mathematical Sketch Generator",
    generated_from: sketch.isGenerated ? "Image Analysis" : sketch.isImported ? "JSON Import" : "Manual Creation",
    version: "3.0"
  };

  return sketchData;
}

// Import sketch from JSON format
export function importSketchFromJSON(jsonData) {
  if (!jsonData.name || !jsonData.description) {
    throw new Error('Invalid sketch file format. Missing required fields (name, description).');
  }

  const importedSketch = {
    ...jsonData,
    category: `Imported (${jsonData.category || 'Unknown'})`,
    isImported: true,
    default_params: jsonData.parameters || jsonData.default_params || {},
    parameter_config: jsonData.parameter_config || []
  };

  return importedSketch;
}

// Generate shape-specific render code
export function generateShapeRenderCode(shapeName, params) {
  if (shapeName.includes("Triangle")) {
    return `
        {/* ✨ HIGHLIGHTED: Triangle using JSON parameters */}
        <polygon 
          points={\`\${params.pointA_x},\${params.pointA_y} \${params.pointB_x},\${params.pointB_y} \${params.pointC_x},\${params.pointC_y}\`} 
          fill={params.fill} 
          stroke={params.stroke} 
          strokeWidth={params.strokeWidth}
        />`;
  } else if (shapeName.includes("Circle")) {
    return `
        {/* ✨ HIGHLIGHTED: Circle using JSON parameters */}
        <circle 
          cx={params.centerX} 
          cy={params.centerY} 
          r={params.radius}
          fill={params.fill} 
          stroke={params.stroke} 
          strokeWidth={params.strokeWidth}
        />`;
  } else if (shapeName.includes("Pyramid")) {
    return `
        {/* ✨ HIGHLIGHTED: 3D Pyramid using JSON parameters */}
        {/* Base and pyramid faces rendered using isometric projection */}
        {/* Parameters: baseSize={params.baseSize}, height={params.height} */}`;
  } else if (shapeName.includes("Parabola") || shapeName.includes("Function")) {
    return `
        {/* ✨ HIGHLIGHTED: Function curve using JSON parameters */}
        <polyline 
          points={curvePoints} 
          fill="none" 
          stroke={params.curveColor} 
          strokeWidth={params.strokeWidth}
        />`;
  } else if (shapeName.includes("Bearing")) {
    return `
        {/* ✨ HIGHLIGHTED: Navigation triangle with compass directions */}
        <polygon 
          points={\`\${params.pointB_x},\${params.pointB_y} \${params.pointL_x},\${params.pointL_y} \${params.pointA_x},\${params.pointA_y}\`} 
          fill="none" 
          stroke={params.triangleColor} 
          strokeWidth={params.strokeWidth}
        />`;
  }
  return "/* Shape render code */";
}

// Generate highlighted React code
export function generateHighlightedCode(sketch, params) {
  const paramsString = JSON.stringify(params, null, 2);
  
  return `import React, { useState } from 'react';

const MathSketchRenderer = ({ showCoords = true, showGrid = true }) => {
  // ✨ HIGHLIGHTED: Current JSON Parameters ${sketch.isGenerated ? '(Generated from Image)' : sketch.isImported ? '(Imported from JSON)' : ''}
  const [params, setParams] = useState(${paramsString});

  const GridPattern = () => (
    <defs>
      <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e0e0e0" strokeWidth="1"/>
      </pattern>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </defs>
  );

  const CartesianAxes = () => (
    <g>
      <line x1="0" y1="150" x2="400" y2="150" stroke="#666" strokeWidth="1"/>
      <line x1="200" y1="0" x2="200" y2="300" stroke="#666" strokeWidth="1"/>
      <polygon points="395,150 385,145 385,155" fill="#666"/>
      <polygon points="200,5 195,15 205,15" fill="#666"/>
      <text x="385" y="140" fontSize="12" fill="#666">X</text>
      <text x="210" y="15" fontSize="12" fill="#666">Y</text>
    </g>
  );

  return (
    <div className="w-full max-w-2xl mx-auto p-4">
      <svg viewBox="0 0 400 300" className="w-full h-64 border border-gray-300">
        {showGrid && <GridPattern />}
        ${generateShapeRenderCode(sketch.name, params)}
        <CartesianAxes />
      </svg>
    </div>
  );
};

export default MathSketchRenderer;`;
}

// Shape pattern detection functions for image analysis
export function hasTriangularPattern(imageData) {
  const data = imageData.data;
  let triangularFeatures = 0;
  
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const brightness = (r + g + b) / 3;
    
    if (brightness < 128) {
      triangularFeatures++;
    }
  }
  
  return triangularFeatures > 1000;
}

export function hasPyramidPattern(imageData) {
  const data = imageData.data;
  let pyramidFeatures = 0;
  
  for (let y = 0; y < 300; y += 10) {
    for (let x = 0; x < 400; x += 10) {
      const i = (y * 400 + x) * 4;
      if (i < data.length) {
        const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
        
        if (brightness < 100) {
          pyramidFeatures++;
        }
      }
    }
  }
  
  return pyramidFeatures > 50;
}

export function hasCirclePattern(imageData) {
  const data = imageData.data;
  let circleFeatures = 0;
  
  for (let y = 0; y < 300; y += 8) {
    for (let x = 0; x < 400; x += 8) {
      const i = (y * 400 + x) * 4;
      if (i < data.length) {
        const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
        
        if (brightness < 120) {
          circleFeatures++;
        }
      }
    }
  }
  
  return circleFeatures > 30 && circleFeatures < 150;
}

export function hasParabolaPattern(imageData) {
  const data = imageData.data;
  let parabolaFeatures = 0;
  
  for (let y = 100; y < 250; y += 10) {
    for (let x = 100; x < 300; x += 10) {
      const i = (y * 400 + x) * 4;
      if (i < data.length) {
        const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
        
        if (brightness < 100) {
          parabolaFeatures++;
        }
      }
    }
  }
  
  return parabolaFeatures > 20 && parabolaFeatures < 80;
}

export function hasFunctionPattern(imageData) {
  const data = imageData.data;
  let functionFeatures = 0;
  
  // Look for horizontal and wavy patterns
  for (let y = 80; y < 220; y += 8) {
    for (let x = 50; x < 350; x += 8) {
      const i = (y * 400 + x) * 4;
      if (i < data.length) {
        const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
        
        if (brightness < 110) {
          functionFeatures++;
        }
      }
    }
  }
  
  return functionFeatures > 40 && functionFeatures < 200;
}

// Enhanced Shape Detection Algorithm
export function detectShapesFromImage(canvas, ctx) {
  const imageData = ctx.getImageData(0, 0, 400, 300);
  const shapes = [];
  
  if (hasTriangularPattern(imageData)) {
    shapes.push({
      id: `detected-triangle-${Date.now()}`,
      category_id: "generated-from-image",
      name: "Detected Triangle",
      description: "Triangle detected from uploaded image",
      coordinates: "Auto-detected vertices",
      type: "2D",
      difficulty_level: "beginner",
      tags: ["triangle", "detected", "ai-generated"],
      default_params: {
        pointA_x: 200, pointA_y: 80,
        pointB_x: 120, pointB_y: 200,
        pointC_x: 280, pointC_y: 200,
        fill: "rgba(100,150,255,0.3)",
        stroke: "blue",
        strokeWidth: 2
      },
      parameter_config: [
        { name: "pointA_x", label: "Point A X", type: "number", min: 0, max: 400 },
        { name: "pointA_y", label: "Point A Y", type: "number", min: 0, max: 300 },
        { name: "pointB_x", label: "Point B X", type: "number", min: 0, max: 400 },
        { name: "pointB_y", label: "Point B Y", type: "number", min: 0, max: 300 },
        { name: "pointC_x", label: "Point C X", type: "number", min: 0, max: 400 },
        { name: "pointC_y", label: "Point C Y", type: "number", min: 0, max: 300 },
        { name: "fill", label: "Fill Color", type: "color" },
        { name: "stroke", label: "Stroke Color", type: "color" },
        { name: "strokeWidth", label: "Stroke Width", type: "number", min: 1, max: 10 }
      ],
      isGenerated: true,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
  }
  
  // Add more shape detection logic here...
  
  return shapes;
}

// Copy to clipboard utility
export function copyToClipboard(text) {
  return navigator.clipboard.writeText(text).then(() => {
    return true;
  }).catch(() => {
    return false;
  });
}

// Download file utility
export function downloadFile(content, filename, contentType = 'application/json') {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Validate sketch data
export function validateSketchData(sketchData) {
  const requiredFields = ['name', 'description', 'type'];
  const missingFields = requiredFields.filter(field => !sketchData[field]);
  
  if (missingFields.length > 0) {
    throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
  }
  
  return true;
}

// Get category name by ID
export function getCategoryNameById(categoryId) {
  const category = SKETCH_CATEGORIES.find(cat => cat.id === categoryId);
  return category ? category.name : 'Unknown Category';
}

// Get category color by ID
export function getCategoryColorById(categoryId) {
  const category = SKETCH_CATEGORIES.find(cat => cat.id === categoryId);
  return category ? category.color : 'gray';
}

// Group sketches by category
export function groupSketchesByCategory(sketches) {
  return sketches.reduce((acc, sketch) => {
    const categoryId = sketch.category_id;
    if (!acc[categoryId]) {
      acc[categoryId] = [];
    }
    acc[categoryId].push(sketch);
    return acc;
  }, {});
}

// Filter sketches by multiple criteria
export function filterSketches(sketches, filters = {}) {
  return sketches.filter(sketch => {
    if (filters.category && sketch.category_id !== filters.category) return false;
    if (filters.type && sketch.type !== filters.type) return false;
    if (filters.difficulty && sketch.difficulty_level !== filters.difficulty) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = 
        sketch.name.toLowerCase().includes(searchLower) ||
        sketch.description.toLowerCase().includes(searchLower) ||
        sketch.tags.some(tag => tag.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }
    return true;
  });
}
